import React, { useEffect, useMemo, useState } from 'react';
import { loadPresets, loadScriptNames, recallPresetId, rememberPresetId } from '../../lib/vmixPresetScripts';

// The Value field for ScriptStart / ScriptStop, where Value is a script NAME.
//
// Typing a script name by hand is the one part of a vMix cue action with no
// feedback until it's on air: vMix silently does nothing for a name that isn't
// in the loaded preset. So offer the names from an uploaded preset instead —
// with free text always one click away, because the preset library is optional
// and a cue may legitimately name a script from a preset nobody has uploaded.
const CUSTOM = ' custom';   // sentinel; can't collide with a script name

export default function ScriptValueField({ value, onChange, inputCls }) {
  const [presets, setPresets] = useState(null);      // null = still loading
  const [presetId, setPresetId] = useState('');
  const [names, setNames] = useState(null);          // null = still loading
  const [typing, setTyping] = useState(false);       // free-text escape hatch

  useEffect(() => {
    let cancelled = false;
    loadPresets().then(list => {
      if (cancelled) return;
      setPresets(list);
      // Last used if it still exists, else the most recently updated one.
      const remembered = recallPresetId();
      const chosen = list.some(p => p.id === remembered) ? remembered : (list[0]?.id || '');
      setPresetId(chosen);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!presetId) { setNames([]); return; }
    let cancelled = false;
    setNames(null);
    loadScriptNames(presetId).then(list => { if (!cancelled) setNames(list); });
    return () => { cancelled = true; };
  }, [presetId]);

  // A name already on the cue that this preset doesn't contain is kept as its
  // own option rather than being silently dropped when the select renders.
  const options = useMemo(() => {
    const list = [...new Set(names || [])];
    const current = (value || '').trim();
    if (current && !list.includes(current)) return [{ name: current, missing: true }, ...list.map(name => ({ name }))];
    return list.map(name => ({ name }));
  }, [names, value]);

  const textField = (
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder="Script name"
      className={`w-36 ${inputCls}`}
    />
  );

  // Still loading, nothing uploaded, or the preset has no scripts: the plain
  // field, which is exactly what this looked like before.
  if (presets === null || presets.length === 0 || (names !== null && options.length === 0)) {
    return (
      <div className="flex items-center gap-1">
        {textField}
        {presets !== null && presets.length === 0 && (
          <span
            className="text-[10px] text-gray-500 cursor-help"
            title="Upload a .vmix in Tools → vMix Script Editor to pick script names from a list."
          >
            ?
          </span>
        )}
      </div>
    );
  }

  if (typing) {
    return (
      <div className="flex items-center gap-1">
        {textField}
        <button
          type="button"
          onClick={() => setTyping(false)}
          title="Pick from the preset's scripts"
          className="text-[10px] text-blue-400 hover:text-blue-300 px-1"
        >
          list
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {/* Which preset the names come from. Hidden when there's only one, since
          then it's not a choice — just noise on every cue row. */}
      {presets.length > 1 && (
        <select
          value={presetId}
          onChange={e => { setPresetId(e.target.value); rememberPresetId(e.target.value); }}
          title="Preset to list scripts from"
          className={`max-w-[9rem] ${inputCls}`}
        >
          {presets.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      )}

      {names === null ? (
        <span className="text-xs text-gray-500 italic px-1">Loading scripts…</span>
      ) : (
        <select
          value={value || ''}
          onChange={e => {
            if (e.target.value === CUSTOM) { setTyping(true); return; }
            onChange(e.target.value);
          }}
          title="Script name (Value)"
          className={`max-w-[12rem] ${inputCls}`}
        >
          <option value="">No script</option>
          {options.map((o, i) => (
            <option key={`${o.name}-${i}`} value={o.name}>
              {o.name}{o.missing ? ' — not in this preset' : ''}
            </option>
          ))}
          <option value={CUSTOM}>Type a name…</option>
        </select>
      )}
    </div>
  );
}
